import { Link, useRouterState } from '@tanstack/react-router';
import { Home, Library, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn.ts';
import { apiJson } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';

interface NavLink {
  to: string;
  icon: typeof Home;
  labelKey: string;
  badgeKey?: 'vocabDue';
}

const studentLinks: NavLink[] = [
  { to: '/student', icon: Home, labelKey: 'nav.home' },
  { to: '/student/vocab', icon: Library, labelKey: 'nav.vocab', badgeKey: 'vocabDue' },
  { to: '/student/profile', icon: User, labelKey: 'nav.profile' },
];

const creatorLinks: NavLink[] = [
  { to: '/creator', icon: Home, labelKey: 'nav.home' },
];

export function BottomNav() {
  const { t } = useTranslation();
  const { user } = useSession();
  const { location } = useRouterState();
  const [vocabDueCount, setVocabDueCount] = useState(0);

  useEffect(() => {
    if (!user || user.role === 'creator') return;
    apiJson<{ entries: unknown[]; dueCount: number }>('/api/student/vocab?due=true&page=1')
      .then((r) => setVocabDueCount(r.dueCount))
      .catch(() => setVocabDueCount(0));
  }, [user]);

  if (!user) return null;
  if (location.pathname.startsWith('/student/lessons/')) return null;

  const links = user.role === 'creator' ? creatorLinks : studentLinks;
  const badges: Record<string, number> = { vocabDue: vocabDueCount };

  return (
    <nav aria-label="Main navigation" className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-border bg-bg-card/80 backdrop-blur-lg px-2 py-1.5 xl:hidden">
      {links.map(({ to, icon: Icon, labelKey, badgeKey }) => {
        const active = location.pathname === to || (to !== '/student' && location.pathname.startsWith(`${to}/`))
          || (to === '/student' && location.pathname === '/student');
        const badge = badgeKey ? badges[badgeKey] ?? 0 : 0;
        return (
          <Link
            key={labelKey}
            to={to}
            className={cn(
              'relative flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-[11px] font-heading font-semibold transition-colors duration-260 ease-[var(--ease-out-expo)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring',
              active ? 'text-accent' : 'text-fg-3 hover:text-fg-2',
            )}
          >
            <Icon className="size-5" strokeWidth={active ? 2.5 : 1.5} />
            {badge > 0 && (
              <span className="absolute -top-0.5 right-0.5 min-w-[16px] h-4 rounded-full bg-error text-white text-[10px] font-heading font-bold flex items-center justify-center px-1">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
            <span>{t(labelKey)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
